import { DateTime } from 'luxon';

/**
 * What the summary's timezone confirm says (`twin-details-page-summary`, R5.5): the title, and the
 * sentence saying how far today's figures move. Pure, so the arithmetic is tested without a DOM.
 *
 * The difference is the two zones' UTC offsets NOW, in hours, rounded to the quarter-hour (every zone
 * in use is a whole quarter-hour off UTC). Zones that share today's offset can still differ on
 * another day, so that case says so rather than *0 hours*.
 */

const MEASURED = 'Opening hours and every daily figure are measured in the twin’s timezone.';

export const zoneChangeTitle = (next: string): string => `Change the timezone to ${next}?`;

/** `4`, `1`, `4.5`, `5.75` — never `4.50`. */
const hours = (n: number): string => `${String(n)} ${n === 1 ? 'hour' : 'hours'}`;

/** The offset of `next` minus that of `previous`, in hours to the quarter, or `null` if unknown. */
export const zoneOffsetDifference = (
  previous: string,
  next: string,
  at: DateTime = DateTime.now(),
): number | null => {
  const a = at.setZone(previous);
  const b = at.setZone(next);
  if (!a.isValid || !b.isValid) return null;
  return Math.round(((b.offset - a.offset) / 60) * 4) / 4;
};

export const zoneChangeBody = (
  previous: string,
  next: string,
  at: DateTime = DateTime.now(),
): string => {
  const diff = zoneOffsetDifference(previous, next, at);
  if (diff === null) return `${MEASURED} Changing it moves them to ${next}.`;
  if (diff === 0) {
    return `${MEASURED} ${next} keeps the same time as ${previous} today, but its clock changes may differ.`;
  }
  const direction = diff > 0 ? 'ahead of' : 'behind';
  return `${MEASURED} ${next} is ${hours(Math.abs(diff))} ${direction} ${previous} now, so they move by that much.`;
};
